import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { DEFAULT_NAV_LAYOUT_MODE, type NavLayoutMode } from '@proffer/shared'
import { useAuth } from '../auth/AuthContext'
import { apiPatch } from '../config/api'
import type { AuthUser } from '../auth/AuthContext'

type NavLayoutContextValue = {
  mode: NavLayoutMode
  setMode: (mode: NavLayoutMode) => void
  saving: boolean
  showTopMenu: boolean
  showSidebar: boolean
}

const NavLayoutContext = createContext<NavLayoutContextValue | null>(null)

export function NavLayoutProvider({ children }: { children: React.ReactNode }) {
  const { user, refreshUser, isLoggedIn } = useAuth()
  const [mode, setModeState] = useState<NavLayoutMode>(DEFAULT_NAV_LAYOUT_MODE)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (user?.navLayout) {
      setModeState(user.navLayout)
      return
    }
    if (!isLoggedIn) {
      setModeState(DEFAULT_NAV_LAYOUT_MODE)
    }
  }, [user?.navLayout, isLoggedIn])

  const setMode = useCallback(
    (next: NavLayoutMode) => {
      setModeState(next)
      if (!isLoggedIn) return

      setSaving(true)
      void (async () => {
        try {
          await apiPatch<AuthUser>('/auth/profile/appearance', { navLayout: next })
          await refreshUser()
        } catch {
          if (user?.navLayout) setModeState(user.navLayout)
        } finally {
          setSaving(false)
        }
      })()
    },
    [isLoggedIn, refreshUser, user?.navLayout],
  )

  const value = useMemo(
    (): NavLayoutContextValue => ({
      mode,
      setMode,
      saving,
      showTopMenu: mode === 'top' || mode === 'both',
      showSidebar: mode === 'sidebar' || mode === 'both',
    }),
    [mode, setMode, saving],
  )

  return (
    <NavLayoutContext.Provider value={value}>{children}</NavLayoutContext.Provider>
  )
}

export function useNavLayout() {
  const ctx = useContext(NavLayoutContext)
  if (!ctx) throw new Error('useNavLayout must be used within NavLayoutProvider')
  return ctx
}
